/**
 * Swarm enemy system — Vampire Survivors-style insectoid hordes.
 *
 * Uses Struct-of-Arrays (SoA) storage with pre-allocated typed arrays
 * for zero GC at runtime. Enemies spawn from fog-of-war edges and
 * converge on the player using simple wall-sliding movement.
 */
import type { Graphics } from 'pixi.js';
import type { Labyrinth } from './Labyrinth';
import { Tile } from './Labyrinth';
import type { ExplorationData } from './ExplorationData';
import { SpatialGrid } from './SpatialGrid';
import { FlowField } from './FlowField';
import type { VfxEffect } from './Projectile';

// ─── Capacity ──────────────────────────────────────────────
const MAX_SWARM = 600;

// ─── Tier stats [index 0 = tier 1, index 4 = tier 5] ──────
const TIER_HP =       [10, 24, 52, 100, 170];
const TIER_DAMAGE =   [4, 10, 20, 36, 60];
const TIER_SPEED =    [2.5, 2.8, 3.2, 3.6, 4.0];
const TIER_ATTACK_CD = [0.8, 0.7, 0.55, 0.4, 0.3];
const TIER_XP =       [1, 3, 7, 14, 25];

// ─── Wave configuration per exploration threshold ──────────
interface WaveConfig {
  threshold: number;   // exploration % to trigger
  duration: number;    // seconds
  spawnRate: number;   // enemies per second
  maxAlive: number;    // cap on simultaneously alive swarm enemies
  tierMin: number;     // min tier (1-5)
  tierMax: number;     // max tier (1-5)
}

const WAVE_CONFIGS: WaveConfig[] = [
  { threshold: 5,  duration: 30, spawnRate: 5,  maxAlive: 100, tierMin: 1, tierMax: 1 },
  { threshold: 10, duration: 40, spawnRate: 8,  maxAlive: 150, tierMin: 1, tierMax: 2 },
  { threshold: 20, duration: 50, spawnRate: 12, maxAlive: 200, tierMin: 1, tierMax: 3 },
  { threshold: 35, duration: 60, spawnRate: 16, maxAlive: 300, tierMin: 2, tierMax: 4 },
  { threshold: 50, duration: 70, spawnRate: 20, maxAlive: 400, tierMin: 2, tierMax: 4 },
  { threshold: 70, duration: 80, spawnRate: 24, maxAlive: 500, tierMin: 3, tierMax: 5 },
  { threshold: 90, duration: 90, spawnRate: 28, maxAlive: 600, tierMin: 4, tierMax: 5 },
];

// ─── Visual constants ──────────────────────────────────────
const TIER_BODY_COLORS = [0x882211, 0x993322, 0xaa3333, 0xbb2244, 0xcc1155];
const TIER_EYE_COLORS = [0xff4400, 0xff5500, 0xff6622, 0xff3344, 0xff2266];
const SPLATTER_COLOR = 0x661100;
const HIT_FLASH_DURATION = 0.1;

// ─── Separation / collision ────────────────────────────────
const SEPARATION_RADIUS = 0.6;
const SEPARATION_FORCE = 2.0;
const ATTACK_RANGE = 0.8;
const STUCK_DESPAWN_TIME = 3.0;
const MIN_SPAWN_DIST = 10; // min tiles from robot to spawn

// ─── Fog-edge cache ────────────────────────────────────────
const FOG_EDGE_REFRESH = 2.0; // seconds between rebuilds

export class SwarmManager {
  // SoA storage — pre-allocated
  readonly x = new Float32Array(MAX_SWARM);
  readonly y = new Float32Array(MAX_SWARM);
  private readonly vx = new Float32Array(MAX_SWARM);
  private readonly vy = new Float32Array(MAX_SWARM);
  readonly hp = new Float32Array(MAX_SWARM);
  private readonly maxHp = new Float32Array(MAX_SWARM);
  private readonly damage = new Float32Array(MAX_SWARM);
  private readonly speed = new Float32Array(MAX_SWARM);
  private readonly attackCd = new Float32Array(MAX_SWARM);
  private readonly attackTimer = new Float32Array(MAX_SWARM);
  private readonly xpReward = new Float32Array(MAX_SWARM);
  readonly angle = new Float32Array(MAX_SWARM);
  readonly alive = new Uint8Array(MAX_SWARM);
  private readonly stuckTimer = new Float32Array(MAX_SWARM);
  readonly wigglePhase = new Float32Array(MAX_SWARM);
  readonly tier = new Uint8Array(MAX_SWARM);
  /** Timer for hit-flash effect (> 0 means flashing white) */
  readonly hitFlash = new Float32Array(MAX_SWARM);
  /** Visibility alpha for LOS fade (1 = visible, 0 = hidden) */
  readonly visAlpha = new Float32Array(MAX_SWARM);
  /** Radar ping countdown — seconds remaining until blip fades */
  readonly radarTimer = new Float32Array(MAX_SWARM);

  // Free list pool
  private readonly freeList: number[] = [];
  aliveCount = 0;

  // Wave state
  waveActive = false;
  private waveTimer = 0;
  private spawnTimer = 0;
  private currentWave: WaveConfig | null = null;
  private readonly triggeredThresholds = new Set<number>();

  // Spatial grid — rebuilt each frame
  readonly grid: SpatialGrid;

  // Fog-edge spawn candidate cache
  private fogEdgeTiles: { x: number; y: number }[] = [];
  private fogEdgeTimer = 0;

  // Flow field for maze-aware pathfinding
  readonly flowField: FlowField;

  // References
  private readonly labyrinth: Labyrinth;
  private readonly exploration: ExplorationData;

  // Death VFX output — caller reads and pushes to robot.vfx
  readonly pendingVfx: VfxEffect[] = [];

  // XP/kills accumulated this frame — caller reads and adds to robot
  frameXpGained = 0;
  frameKills = 0;
  /** Per-tier kill counters for this frame (index 0 unused; tiers 1..5). */
  readonly frameKillsByTier: number[] = [0, 0, 0, 0, 0, 0];

  constructor(labyrinth: Labyrinth, exploration: ExplorationData) {
    this.labyrinth = labyrinth;
    this.exploration = exploration;
    this.grid = new SpatialGrid(2, labyrinth.width);
    this.flowField = new FlowField(labyrinth);

    // Initialize free list (all slots available)
    for (let i = MAX_SWARM - 1; i >= 0; i--) {
      this.freeList.push(i);
    }
  }

  // ─── Wave trigger ──────────────────────────────────────────

  checkTrigger(explorationPct: number) {
    for (const cfg of WAVE_CONFIGS) {
      if (explorationPct >= cfg.threshold && !this.triggeredThresholds.has(cfg.threshold)) {
        this.startWave(cfg);
        this.triggeredThresholds.add(cfg.threshold);
        break; // one wave at a time
      }
    }
  }

  private startWave(cfg: WaveConfig) {
    this.waveActive = true;
    this.waveTimer = cfg.duration;
    this.spawnTimer = 0;
    this.currentWave = cfg;
    // Force immediate fog-edge refresh
    this.fogEdgeTimer = FOG_EDGE_REFRESH;
  }

  // ─── Main update ───────────────────────────────────────────

  update(dt: number, robotX: number, robotY: number, robotDamageReduction: number, applyDamageToRobot: (dmg: number) => void, vfxOut: VfxEffect[]) {
    this.frameXpGained = 0;
    this.frameKills = 0;
    for (let t = 0; t < this.frameKillsByTier.length; t++) this.frameKillsByTier[t] = 0;

    // Recompute flow field when player changes tile.
    // Skip entirely when no swarm is alive — the field has no consumer.
    // `spawn()` invalidates the cached tile so the first spawn gets a fresh field.
    if (this.aliveCount > 0) {
      this.flowField.update(this.labyrinth, robotX, robotY);
    }

    // Rebuild spatial grid
    this.grid.clear();
    for (let i = 0; i < MAX_SWARM; i++) {
      if (this.alive[i]) {
        this.grid.insert(i, this.x[i]!, this.y[i]!);
      }
    }

    // Spawning
    if (this.waveActive) {
      this.waveTimer -= dt;
      if (this.waveTimer <= 0) {
        this.waveActive = false;
        this.currentWave = null;
      } else {
        this.updateSpawning(dt, robotX, robotY);
      }
    }

    // Movement + attack for all alive swarm enemies
    for (let i = 0; i < MAX_SWARM; i++) {
      if (!this.alive[i]) continue;

      // Hit flash decay
      if (this.hitFlash[i]! > 0) {
        this.hitFlash[i]! -= dt;
        if (this.hitFlash[i]! < 0) this.hitFlash[i] = 0;
      }

      // Direction to robot
      const dx = robotX - this.x[i]!;
      const dy = robotY - this.y[i]!;
      const dist = Math.sqrt(dx * dx + dy * dy);

      // Attack check
      if (dist < ATTACK_RANGE) {
        this.attackTimer[i]! -= dt;
        if (this.attackTimer[i]! <= 0) {
          this.attackTimer[i] = this.attackCd[i]!;
          const dmg = this.damage[i]! * (1 - robotDamageReduction);
          applyDamageToRobot(dmg);
        }
      } else {
        // Move toward robot with wall-sliding
        this.moveToward(i, dx, dy, dist, dt);
      }
    }

    // Flush pending VFX
    for (let i = 0; i < this.pendingVfx.length; i++) {
      vfxOut.push(this.pendingVfx[i]!);
    }
    this.pendingVfx.length = 0;
  }

  // ─── Movement via flow field ───────────────────────────────

  private moveToward(i: number, dx: number, dy: number, dist: number, dt: number) {
    if (dist < 0.01) return;

    const spd = this.speed[i]!;
    const ix = this.x[i]!;
    const iy = this.y[i]!;

    // Use flow field for maze-aware direction.
    // When very close to the player (< 3 tiles) blend in direct vector
    // so final approach isn't tile-snappy.
    const ff = this.flowField;
    const tx = Math.floor(ix);
    const ty = Math.floor(iy);
    const idx = ty * ff.w + tx;
    let fx = ff.dirX[idx]!;
    let fy = ff.dirY[idx]!;

    // If tile is unreachable (no flow), fall back to direct vector
    if (fx === 0 && fy === 0) {
      fx = dx / dist;
      fy = dy / dist;
    } else if (dist < 3) {
      // Blend: more direct as we get closer
      const blend = dist / 3; // 0 at player, 1 at 3 tiles
      const directX = dx / dist;
      const directY = dy / dist;
      fx = fx * blend + directX * (1 - blend);
      fy = fy * blend + directY * (1 - blend);
      // Re-normalize
      const fl = Math.sqrt(fx * fx + fy * fy);
      if (fl > 0.001) { fx /= fl; fy /= fl; }
    }

    // Separation force from nearby swarm enemies
    let sepX = 0, sepY = 0;
    const neighbors = this.grid.query(ix, iy, SEPARATION_RADIUS);
    for (let n = 0; n < neighbors.length; n++) {
      const j = neighbors[n]!;
      if (j === i || !this.alive[j]) continue;
      const sdx = ix - this.x[j]!;
      const sdy = iy - this.y[j]!;
      const sd = sdx * sdx + sdy * sdy;
      if (sd < SEPARATION_RADIUS * SEPARATION_RADIUS && sd > 0.001) {
        const invD = 1 / Math.sqrt(sd);
        sepX += sdx * invD * SEPARATION_FORCE;
        sepY += sdy * invD * SEPARATION_FORCE;
      }
    }

    // Combined direction
    let mx = fx * spd + sepX;
    let my = fy * spd + sepY;
    const ml = Math.sqrt(mx * mx + my * my);
    if (ml > spd) {
      mx = mx / ml * spd;
      my = my / ml * spd;
    }

    const newX = ix + mx * dt;
    const newY = iy + my * dt;

    const lab = this.labyrinth;

    // Try full move
    if (lab.isWalkable(Math.floor(newX), Math.floor(newY))) {
      this.x[i] = newX;
      this.y[i] = newY;
      this.stuckTimer[i] = 0;
    }
    // Try sliding along X
    else if (lab.isWalkable(Math.floor(newX), Math.floor(iy))) {
      this.x[i] = newX;
      this.stuckTimer[i] = 0;
    }
    // Try sliding along Y
    else if (lab.isWalkable(Math.floor(ix), Math.floor(newY))) {
      this.y[i] = newY;
      this.stuckTimer[i] = 0;
    }
    // Fully blocked — increment stuck timer
    else {
      this.stuckTimer[i]! += dt;
      if (this.stuckTimer[i]! > STUCK_DESPAWN_TIME) {
        // Respawn at a new fog-edge position
        this.respawnAtFogEdge(i);
      }
    }

    // Update facing angle — use movement direction for natural look
    this.angle[i] = Math.atan2(my, mx);
  }

  // ─── Spawning ─────────────────────────────────────────────

  private updateSpawning(dt: number, robotX: number, robotY: number) {
    if (!this.currentWave) return;

    // Refresh fog-edge cache periodically
    this.fogEdgeTimer += dt;
    if (this.fogEdgeTimer >= FOG_EDGE_REFRESH) {
      this.fogEdgeTimer = 0;
      this.rebuildFogEdgeCache(robotX, robotY);
    }

    if (this.fogEdgeTileCount === 0) return;

    this.spawnTimer += dt;
    const interval = 1.0 / this.currentWave.spawnRate;

    while (this.spawnTimer >= interval && this.aliveCount < this.currentWave.maxAlive && this.freeList.length > 0) {
      this.spawnTimer -= interval;
      const pos = this.fogEdgeTiles[Math.floor(Math.random() * this.fogEdgeTileCount)]!;
      const tierRange = this.currentWave.tierMax - this.currentWave.tierMin;
      const t = this.currentWave.tierMin + Math.floor(Math.random() * (tierRange + 1));
      this.spawn(pos.x + 0.5, pos.y + 0.5, Math.min(t, 5));
    }
  }

  private fogEdgeTileCount = 0;

  private rebuildFogEdgeCache(robotX: number, robotY: number) {
    this.fogEdgeTileCount = 0;
    const lab = this.labyrinth;
    const exp = this.exploration;
    const w = lab.width;
    const h = lab.height;
    const minDistSq = MIN_SPAWN_DIST * MIN_SPAWN_DIST;

    // Scan tiles: walkable, not revealed, adjacent to at least one revealed tile
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        if (lab.tiles[y]![x] === Tile.Wall) continue; // wall
        if (exp.isRevealed(x, y)) continue;  // already visible

        // Check distance from robot
        const dx = x - robotX;
        const dy = y - robotY;
        if (dx * dx + dy * dy < minDistSq) continue;

        // Check adjacency to revealed tile
        if (exp.isRevealed(x - 1, y) || exp.isRevealed(x + 1, y) ||
            exp.isRevealed(x, y - 1) || exp.isRevealed(x, y + 1)) {
          const idx = this.fogEdgeTileCount;
          if (idx < this.fogEdgeTiles.length) {
            const tile = this.fogEdgeTiles[idx]!;
            tile.x = x;
            tile.y = y;
          } else {
            this.fogEdgeTiles.push({ x, y });
          }
          this.fogEdgeTileCount++;
        }
      }
    }
  }

  // ─── Pool operations ──────────────────────────────────────

  private spawn(x: number, y: number, tierVal: number): number {
    if (this.freeList.length === 0) return -1;
    // First swarm after a quiet period — force a fresh flow field on next update().
    if (this.aliveCount === 0) this.flowField.invalidate();
    const i = this.freeList.pop()!;
    const t = tierVal - 1; // 0-indexed for arrays

    this.x[i] = x;
    this.y[i] = y;
    this.vx[i] = 0;
    this.vy[i] = 0;
    this.hp[i] = TIER_HP[t]!;
    this.maxHp[i] = TIER_HP[t]!;
    this.damage[i] = TIER_DAMAGE[t]!;
    this.speed[i] = TIER_SPEED[t]!;
    this.attackCd[i] = TIER_ATTACK_CD[t]!;
    this.attackTimer[i] = TIER_ATTACK_CD[t]!; // start on cooldown
    this.xpReward[i] = TIER_XP[t]!;
    this.angle[i] = Math.random() * Math.PI * 2;
    this.alive[i] = 1;
    this.stuckTimer[i] = 0;
    this.wigglePhase[i] = Math.random() * Math.PI * 2;
    this.tier[i] = tierVal;
    this.hitFlash[i] = 0;
    this.aliveCount++;

    return i;
  }

  kill(i: number) {
    if (!this.alive[i]) return;
    this.alive[i] = 0;
    this.aliveCount--;
    this.freeList.push(i);
    this.frameXpGained += this.xpReward[i]!;
    this.frameKills++;
    const t = this.tier[i] ?? 0;
    if (t >= 1 && t <= 5) this.frameKillsByTier[t]!++;

    // Splatter VFX
    this.pendingVfx.push({
      kind: 'death_explosion',
      x: this.x[i]!,
      y: this.y[i]!,
      radius: 0.4,
      life: 0.3,
      maxLife: 0.3,
      color: SPLATTER_COLOR,
    });
  }

  /**
   * Apply damage to a swarm enemy. Returns true if killed.
   */
  damage_enemy(i: number, dmg: number): boolean {
    if (!this.alive[i]) return false;
    this.hp[i]! -= dmg;
    this.hitFlash[i] = HIT_FLASH_DURATION;
    if (this.hp[i]! <= 0) {
      this.hp[i] = 0;
      this.kill(i);
      return true;
    }
    return false;
  }

  private respawnAtFogEdge(i: number) {
    if (this.fogEdgeTileCount === 0) {
      // No valid spawn points — just kill it
      this.alive[i] = 0;
      this.aliveCount--;
      this.freeList.push(i);
      return;
    }
    const pos = this.fogEdgeTiles[Math.floor(Math.random() * this.fogEdgeTileCount)]!;
    this.x[i] = pos.x + 0.5;
    this.y[i] = pos.y + 0.5;
    this.stuckTimer[i] = 0;
  }

  // ─── Queries ──────────────────────────────────────────────

  /**
   * Find the nearest alive swarm enemy within radius.
   * Returns index or -1 if none found.
   */
  findNearest(px: number, py: number, radius: number): number {
    const candidates = this.grid.query(px, py, radius);
    let bestIdx = -1;
    let bestDistSq = radius * radius;

    for (let n = 0; n < candidates.length; n++) {
      const i = candidates[n]!;
      if (!this.alive[i]) continue;
      const dx = this.x[i]! - px;
      const dy = this.y[i]! - py;
      const dsq = dx * dx + dy * dy;
      if (dsq < bestDistSq) {
        bestDistSq = dsq;
        bestIdx = i;
      }
    }

    return bestIdx;
  }

  // ─── Rendering ────────────────────────────────────────────

  draw(
    gBehind: Graphics, gInFront: Graphics, robotY: number,
    _exploration: ExplorationData, t: number, zoom: number, tileSize: number,
    acquireFadeGfx?: (alpha: number, behind: boolean) => Graphics,
  ) {
    for (let i = 0; i < MAX_SWARM; i++) {
      if (!this.alive[i]) continue;

      // Visibility check — skip invisible enemies
      const va = this.visAlpha[i]!;
      if (va <= 0) continue;

      const ix = this.x[i]!;
      const iy = this.y[i]!;
      const px = ix * tileSize;
      const py = iy * tileSize;
      const behind = iy < robotY;

      // Fully visible: shared Graphics; fading: pooled per-enemy Graphics
      let g: Graphics;
      if (va >= 1) {
        g = behind ? gBehind : gInFront;
      } else if (acquireFadeGfx) {
        g = acquireFadeGfx(va, behind);
      } else {
        g = behind ? gBehind : gInFront;
      }
      const tierIdx = this.tier[i]! - 1;

      // Hit flash: override color to white briefly
      const flashing = this.hitFlash[i]! > 0;
      const bodyColor = flashing ? 0xffffff : TIER_BODY_COLORS[tierIdx]!;
      const eyeColor = flashing ? 0xffffff : TIER_EYE_COLORS[tierIdx]!;

      const wiggle = Math.sin(t * 8 + this.wigglePhase[i]!) * 0.15;
      const a = this.angle[i]! + wiggle;
      const cs = Math.cos(a);
      const sn = Math.sin(a);

      if (zoom < 0.3) {
        // LOD: simple dot
        g.circle(px, py, 2);
        g.fill(bodyColor);
        continue;
      }

      // Triangular body
      const s = tileSize * 0.525;
      // Front point
      const fx = px + cs * s * 1.2;
      const fy = py + sn * s * 1.2;
      // Back-left
      const blx = px + (-cs * s * 0.8 - sn * s * 0.7);
      const bly = py + (-sn * s * 0.8 + cs * s * 0.7);
      // Back-right
      const brx = px + (-cs * s * 0.8 + sn * s * 0.7);
      const bry = py + (-sn * s * 0.8 - cs * s * 0.7);

      g.moveTo(fx, fy);
      g.lineTo(blx, bly);
      g.lineTo(brx, bry);
      g.closePath();
      g.fill(bodyColor);
      g.stroke({ color: 0x220800, width: 0.5 });

      // Eyes (two dots near front)
      const eyeOffset = s * 0.4;
      const eyeR = Math.max(1, s * 0.15);
      const e1x = px + cs * eyeOffset - sn * eyeR * 2;
      const e1y = py + sn * eyeOffset + cs * eyeR * 2;
      const e2x = px + cs * eyeOffset + sn * eyeR * 2;
      const e2y = py + sn * eyeOffset - cs * eyeR * 2;

      const eyePulse = 0.6 + Math.sin(t * 6 + this.wigglePhase[i]!) * 0.4;
      g.circle(e1x, e1y, eyeR);
      g.fill({ color: eyeColor, alpha: eyePulse });
      g.circle(e2x, e2y, eyeR);
      g.fill({ color: eyeColor, alpha: eyePulse });

      if (zoom >= 0.6) {
        // Legs — two short lines at sides
        const legLen = s * 0.5;
        const legWiggle = Math.sin(t * 12 + this.wigglePhase[i]!) * s * 0.3;

        // Left leg
        const llx = px - sn * s * 0.5;
        const lly = py + cs * s * 0.5;
        g.moveTo(llx, lly);
        g.lineTo(llx - sn * legLen + cs * legWiggle, lly + cs * legLen + sn * legWiggle);
        g.stroke({ color: bodyColor, width: 0.5, alpha: 0.6 });

        // Right leg
        const rlx = px + sn * s * 0.5;
        const rly = py - cs * s * 0.5;
        g.moveTo(rlx, rly);
        g.lineTo(rlx + sn * legLen + cs * legWiggle, rly - cs * legLen + sn * legWiggle);
        g.stroke({ color: bodyColor, width: 0.5, alpha: 0.6 });
      }
    }
  }
}
